export type LatexResult = { mainLine: string; rateLine: string | null };

const convertSqrtToLatex = (expr: string): string => {
  let result = '';
  let i = 0;

  while (i < expr.length) {
    if (expr.substring(i, i + 5) === 'sqrt(') {
      result += '\\sqrt{(';
      i += 5;

      let depth = 1;
      let start = i;
      while (i < expr.length && depth > 0) {
        if (expr[i] === '(') depth++;
        else if (expr[i] === ')') depth--;
        if (depth > 0) i++;
      }

      const content = convertSqrtToLatex(expr.substring(start, i));
      result += content;

      if (i < expr.length && expr[i] === ')') {
        result += ')}';
        i++;
      } else {
        result += '}';
      }
    } else {
      result += expr[i];
      i++;
    }
  }

  return result;
};

const convertPowerToLatex = (expr: string): string => {
  let result = '';
  let i = 0;

  while (i < expr.length) {
    if (i < expr.length - 1 && expr[i] === '^' && expr[i + 1] === '(') {
      result += '^{';
      i += 2;

      let depth = 1;
      let start = i;
      while (i < expr.length && depth > 0) {
        if (expr[i] === '(') depth++;
        else if (expr[i] === ')') depth--;
        if (depth > 0) i++;
      }

      const content = convertPowerToLatex(expr.substring(start, i));
      result += content;

      if (i < expr.length && expr[i] === ')') {
        result += '}';
        i++;
      }
    } else {
      result += expr[i];
      i++;
    }
  }

  return result;
};

const convertExponentialToLatex = (expr: string): string => {
  let result = '';
  let i = 0;

  while (i < expr.length) {
    if (expr.substring(i, i + 3) === 'e^(') {
      result += 'e^{';
      i += 3;

      let depth = 1;
      let start = i;
      while (i < expr.length && depth > 0) {
        if (expr[i] === '(') depth++;
        else if (expr[i] === ')') depth--;
        if (depth > 0) i++;
      }

      const content = convertExponentialToLatex(expr.substring(start, i));
      result += content;

      if (i < expr.length && expr[i] === ')') {
        result += '}';
        i++;
      }
    } else {
      result += expr[i];
      i++;
    }
  }

  return result;
};

const convertReciprocalToLatex = (expr: string): string => {
  let result = '';
  let i = 0;

  while (i < expr.length) {
    if (expr.substring(i, i + 3) === '1/(') {
      result += '\\frac{1}{';
      i += 3;

      let depth = 1;
      let start = i;
      while (i < expr.length && depth > 0) {
        if (expr[i] === '(') depth++;
        else if (expr[i] === ')') depth--;
        if (depth > 0) i++;
      }

      const content = convertReciprocalToLatex(expr.substring(start, i));
      result += content;

      if (i < expr.length && expr[i] === ')') {
        result += '}';
        i++;
      }
    } else {
      result += expr[i];
      i++;
    }
  }

  return result;
};

const addThousandsSeparators = (digits: string): string => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const formatNumberLiteralForLatex = (literal: string): string => {
  // Only format for display (no rounding). Keep scientific notation unchanged.
  if (!literal || /[eE]/.test(literal)) return literal;

  const sign = literal.startsWith('-') ? '-' : '';
  const unsigned = sign ? literal.slice(1) : literal;
  const [integerPart, fractionalPart] = unsigned.split('.');

  // Avoid formatting leading-zero integers like "0001" (not expected from calculator, but safer)
  const shouldGroup = integerPart.length > 3 && !(integerPart.length > 1 && integerPart.startsWith('0'));
  if (!shouldGroup) return literal;

  const groupedIntegerPart = addThousandsSeparators(integerPart);
  const formatted =
    fractionalPart !== undefined ? `${sign}${groupedIntegerPart}.${fractionalPart}` : `${sign}${groupedIntegerPart}`;

  // In KaTeX math mode, commas add punctuation spacing; `{,}` renders a literal comma.
  return formatted.replace(/,/g, '{,}');
};

const formatNumericLiteralsForLatex = (expr: string): string => {
  if (!expr) return expr;

  // Match a number that begins at start OR after a non-identifier character (space, operator, "=", "(" ...)
  // so we don't accidentally touch parts of words like "sqrt" or "pi".
  const NUMBER_TOKEN = /(^|[^0-9A-Za-z_.])(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

  return expr.replace(NUMBER_TOKEN, (_match, prefix: string, literal: string) => {
    return `${prefix}${formatNumberLiteralForLatex(literal)}`;
  });
};

const addDegreesToTrigNumericArgs = (latex: string): string => {
  // KaTeX expects degrees as `°`.
  // Only add degrees when the argument is a *plain numeric literal* (optionally with `{,}` grouping and decimals).
  // Examples:
  //   \sin(45)     -> \sin(45°)
  //   \cos(-30.5)  -> \cos(-30.5°)
  //   \sin(\pi/2)  -> unchanged
  //   \sin(45°) -> unchanged
  const NUMBER_IN_LATEX = '-?\\d+(?:\\{,\\}\\d{3})*(?:\\.\\d+)?'; // supports 1{,}000.5 style too

  const trigFns = ['sin', 'cos', 'tan', 'csc', 'sec', 'cot'];
  const trigPattern = new RegExp(
    `\\\\(?:${trigFns.join('|')})\\((${NUMBER_IN_LATEX})\\)(?!\\s*\\^\\{\\\\circ\\})`,
    'g'
  );

  return latex.replace(trigPattern, (match, num: string) => match.replace(`(${num})`, `(${num}^{\\circ})`));
};

// All converter unit codes from CalculatorConfigs
// Ordered by length (longest first) to avoid partial matches
const getAllConverterUnitCodes = (): string[] => {
  const units = [
    // Units with special characters (longest first)
    'km/h',
    'm/s',
    'ft/s',
    'mph',
    'km²',
    'ft²',
    'm²',
    'm³',
    'ft³', // Unicode superscripts
    'km^2',
    'ft^2',
    'm^2',
    'm^3',
    'ft^3', // LaTeX superscripts
    'degC',
    'degF',
    '°C',
    '°F',
    // Multi-character units
    'acre',
    'grad',
    'inch',
    'hour',
    'byte',
    'day',
    'min',
    'USD',
    'EUR',
    'GBP',
    'NIS',
    'JPY',
    'GB',
    'TB',
    'MB',
    'KB',
    'kg',
    'ml',
    'ft',
    'mi',
    'km',
    'cm',
    'mm',
    'ha',
    'oz',
    'lb',
    'gal',
    'ms',
    'deg',
    'rad',
    // Single-letter units (be careful with word boundaries)
    'B',
    'K',
    'L',
    'h',
    'g',
    'm',
    's',
  ];

  // Sort by length (longest first) to avoid partial matches
  return units.sort((a, b) => b.length - a.length);
};

// Escape special regex characters in unit codes
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const formatConverterUnitsForLatex = (latex: string): string => {
  if (!latex) return latex;

  // Get all unit codes, sorted by length (longest first) to avoid partial matches
  const allUnits = getAllConverterUnitCodes();

  // Numbers at this stage may contain `{,}` groups and decimals.
  const LATEX_NUMBER = '-?\\d+(?:\\{,\\}\\d{3})*(?:\\.\\d+)?';

  // Some composite "per" units (e.g. "km/h", "ft/s") should remain raw in the display.
  // Without protecting them first, the single-letter pass would wrap the denominator ("h"/"s")
  // and we'd end up with "60km/\\text{h}", which the UI/tests don't want.
  //
  // Rule: protect slash-units where at least one side is multi-letter (so "m/s" still formats).
  const protectedSlashUnits = allUnits.filter((u) => {
    if (!u.includes('/')) return false;
    const [num, den] = u.split('/');
    return (num?.length ?? 0) > 1 || (den?.length ?? 0) > 1;
  });

  const protectedMap = new Map<string, string>();
  let protectedIndex = 0;
  for (const unit of protectedSlashUnits) {
    const token = `__SFU_UNIT_${protectedIndex++}__`;
    protectedMap.set(token, unit);
    latex = latex.replace(new RegExp(escapeRegex(unit), 'g'), token);
  }

  // Process each unit code (longest first to avoid partial matches)
  for (const unit of allUnits) {
    const escapedUnit = escapeRegex(unit);

    // Strategy: Match "number + unit" together first, then format both
    // This ensures spacing is always added correctly

    let unitPattern: string;
    if (unit.length === 1) {
      // Single letter: must not be preceded or followed by another letter
      // Match at end of string, or followed by space, =, or other non-letter
      unitPattern = `(?<![a-zA-Z])${escapedUnit}(?![a-zA-Z])`;
    } else {
      // Multi-char: use word boundaries
      unitPattern = `\\b${escapedUnit}\\b`;
    }

    // 1) Match "number + optional spaces + unit" and replace with "number + space + \text{unit}"
    // This handles both spacing and wrapping in one step
    latex = latex.replace(
      new RegExp(`(${LATEX_NUMBER})\\s*(${unitPattern})(?!\\\\text\\{)`, 'g'),
      (match, number, unitMatch) => {
        // Skip if already inside \text{}
        if (match.includes('\\text{')) return match;
        return `${number}\\ \\text{${unitMatch}}`;
      }
    );

    // 2) Also handle units that appear standalone (not immediately after a number)
    // Wrap them in \text{} if not already wrapped
    if (unit.length === 1) {
      latex = latex.replace(
        new RegExp(`(?<!\\\\text\\{)(?<![a-zA-Z\\d])(${escapedUnit})(?![a-zA-Z])(?!\\})`, 'g'),
        '\\text{$1}'
      );
    } else {
      latex = latex.replace(new RegExp(`(?<!\\\\text\\{)\\b(${escapedUnit})\\b(?!\\})`, 'g'), '\\text{$1}');
    }
  }

  // Restore protected composite units.
  for (const [token, unit] of protectedMap.entries()) {
    latex = latex.replace(new RegExp(escapeRegex(token), 'g'), unit);
  }

  return latex;
};

// Returns an object with mainLine and rateLine if Rate is present, otherwise returns the original string
const splitRateFromMainLine = (latex: string): { mainLine: string; rateLine: string | null } => {
  if (!latex) return { mainLine: latex, rateLine: null };

  const rateRegex = /\(\s*Rate\s*:\s*([^)]+)\)/i;
  const match = latex.match(rateRegex);
  if (!match) return { mainLine: latex, rateLine: null };

  const rateValue = match[1].trim();
  const mainLine = latex.replace(rateRegex, '').trim();
  // Wrap Rate in \text{} so it renders as upright text (not italic)
  const rateLine = `\\text{(Rate: ${rateValue})}`;

  return { mainLine, rateLine };
};

export const convertToLatex = (expression: string): string => {
  if (!expression || expression === '0') return expression;

  // Normalize: some locales (e.g. via toLocaleString) add a space after thousands comma.
  // Remove so shared chat result matches Basic/Scientific (no space after comma).
  const normalized = expression.replace(/,\s+/g, ',');

  let latex = formatNumericLiteralsForLatex(normalized);
  // In KaTeX, a comma adds punctuation spacing; {,} renders a literal comma with no space.
  // Replace commas between digits (thousands separators) so shared chat has no extra space.
  latex = latex.replace(/(?<=\d),(?=\d)/g, '{,}');
  latex = formatConverterUnitsForLatex(latex);

  latex = convertSqrtToLatex(latex);

  latex = latex.replace(/(?<!\\)sqrt(?!\()/g, '\\sqrt{}');

  latex = latex.replace(/sin\(/g, '\\sin(');
  latex = latex.replace(/cos\(/g, '\\cos(');
  latex = latex.replace(/tan\(/g, '\\tan(');
  latex = latex.replace(/asin\(/g, '\\arcsin(');
  latex = latex.replace(/acos\(/g, '\\arccos(');
  latex = latex.replace(/atan\(/g, '\\arctan(');
  latex = latex.replace(/csc\(/g, '\\csc(');
  latex = latex.replace(/sec\(/g, '\\sec(');
  latex = latex.replace(/cot\(/g, '\\cot(');

  latex = addDegreesToTrigNumericArgs(latex);

  latex = latex.replace(/\bpi\b/g, '\\pi');

  latex = convertExponentialToLatex(latex);

  latex = convertPowerToLatex(latex);

  latex = latex.replace(/\^(\d+)/g, '^{$1}');
  latex = latex.replace(/\^([a-zA-Z])/g, '^{$1}');

  latex = convertReciprocalToLatex(latex);

  latex = latex.replace(/!/g, '!');
  latex = latex.replace(/%/g, '\\%');
  latex = latex.replace(/±/g, '\\pm');

  return latex;
};

// Helper function for MessageBubble to split converter messages with Rate
export const splitConverterMessage = (latex: string): { mainLine: string; rateLine: string | null } => {
  const separator = '|||RATE_SEPARATOR|||';
  if (latex.includes(separator)) {
    const [mainLine, rateLine] = latex.split(separator);
    return { mainLine, rateLine };
  }
  return { mainLine: latex, rateLine: null };
};

export const hasScientificNotation = (expression: string): boolean => {
  const scientificPatterns = [
    /sin/,
    /cos/,
    /tan/,
    /csc/,
    /sec/,
    /cot/,
    /sqrt/,
    /pi/,
    /\^/,
    /!/,
    /±/,
    /e\^/,
    /1\//,
    /e/,
  ];

  return scientificPatterns.some((pattern) => pattern.test(expression));
};

// Converter outputs may include "(Rate: ...)" — this function returns it separately
// so the UI can render it on a new line without relying on magic string separators
export const convertToLatexWithRate = (expression: string): LatexResult => {
  const latex = convertToLatex(expression);
  return splitRateFromMainLine(latex);
};
